import { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Check, X, UserPlus, Clock } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

export default function SignupRequestsCard({ onApproved }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [approveId, setApproveId] = useState(null);
  const [rejectId, setRejectId] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/dev/signup-requests");
      setItems(data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur de chargement");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const approve = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/dev/signup-requests/${approveId}/approve`);
      toast.success(`Compte activé : ${data.admin_email}`);
      setApproveId(null);
      await load();
      onApproved?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Approbation échouée");
    } finally {
      setBusy(false);
    }
  };

  const reject = async () => {
    setBusy(true);
    try {
      await api.post(`/dev/signup-requests/${rejectId}/reject`, {});
      toast.success("Demande rejetée");
      setRejectId(null);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Rejet échoué");
    } finally {
      setBusy(false);
    }
  };

  if (loading) return null;
  if (items.length === 0) return null;

  return (
    <Card className="mb-6 rounded-xl border-2 border-orange-300 bg-orange-50/60 shadow-md shadow-orange-900/10 overflow-hidden" data-testid="signup-requests-card">
      <div className="px-4 py-3 border-b border-orange-200 bg-orange-100/60 flex items-center gap-3">
        <div className="w-8 h-8 rounded-md bg-orange-500 flex items-center justify-center text-white shadow-sm">
          <UserPlus className="w-4 h-4" />
        </div>
        <div className="flex-1">
          <div className="font-heading font-semibold text-orange-950 flex items-center gap-2">
            Demandes d'inscription en attente
            <Badge className="bg-orange-500 text-white hover:bg-orange-500 text-xs" data-testid="signup-pending-count">{items.length}</Badge>
          </div>
          <div className="text-xs text-orange-900/70 mt-0.5">
            Une nouvelle société sera créée automatiquement à l'approbation, avec le compte administrateur fourni.
          </div>
        </div>
      </div>

      <div className="divide-y divide-orange-200/60">
        {items.map((r) => (
          <div key={r.id} className="p-4 flex flex-col md:flex-row md:items-center gap-3 md:gap-4" data-testid={`signup-row-${r.id}`}>
            <div className="flex-1 min-w-0">
              <div className="font-heading font-semibold text-slate-900 truncate">{r.society_nom}</div>
              {r.contact_nom && (
                <div className="text-sm font-medium text-slate-700 truncate" data-testid={`signup-contact-${r.id}`}>
                  {r.contact_nom}
                </div>
              )}
              <div className="text-sm text-slate-600 break-all">
                <span className="font-medium">{r.email}</span>
                {r.telephone && (
                  <>
                    <span className="mx-2 text-slate-300">·</span>
                    <span className="font-mono text-slate-700" data-testid={`signup-phone-${r.id}`}>{r.telephone}</span>
                  </>
                )}
                <span className="mx-2 text-slate-300">·</span>
                <Clock className="inline w-3 h-3 mr-1 -mt-0.5 text-slate-400" />
                <span className="text-slate-500">{fmtDate(r.created_at)}</span>
              </div>
            </div>
            <div className="flex gap-2 shrink-0">
              <Button
                size="sm"
                variant="outline"
                onClick={() => setRejectId(r.id)}
                className="border-rose-300 text-rose-700 hover:bg-rose-50 hover:text-rose-800"
                data-testid={`btn-signup-reject-${r.id}`}
              >
                <X className="w-4 h-4 mr-1" /> Rejeter
              </Button>
              <Button
                size="sm"
                onClick={() => setApproveId(r.id)}
                className="bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm"
                data-testid={`btn-signup-approve-${r.id}`}
              >
                <Check className="w-4 h-4 mr-1" /> Approuver
              </Button>
            </div>
          </div>
        ))}
      </div>

      <AlertDialog open={!!approveId} onOpenChange={(o) => !o && setApproveId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Approuver cette demande ?</AlertDialogTitle>
            <AlertDialogDescription>
              La société sera créée immédiatement (durée d'abonnement par défaut : 3 mois) et le compte administrateur sera activé avec les identifiants fournis par le demandeur.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={approve} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700" data-testid="btn-confirm-signup-approve">
              {busy ? "Activation…" : "Approuver et activer"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!rejectId} onOpenChange={(o) => !o && setRejectId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Rejeter cette demande ?</AlertDialogTitle>
            <AlertDialogDescription>
              La demande sera archivée. Le demandeur pourra soumettre une nouvelle demande depuis la page d'inscription.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy}>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={reject} disabled={busy} className="bg-rose-600 hover:bg-rose-700" data-testid="btn-confirm-signup-reject">
              {busy ? "Rejet…" : "Rejeter"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}
